import {
  ChangeDetectionStrategy,
  ChangeDetectorRef,
  Component,
  OnDestroy,
  OnInit,
} from '@angular/core';
import { MatDialog } from '@angular/material/dialog';
import {
  PVExcessPriorityListItem,
  FeatureNames,
  FeaturePropertyKey,
  FeatureCommands,
  DeviceIds,
} from '@vi-iot/client/index';
import { PvPowerLearnMoreModalComponent } from './pv-power-learn-more-modal/pv-power-learn-more-modal.component';
import { Observable, Subject, first, switchMap, takeUntil } from 'rxjs';
import { HemsStore, InstallationFeaturesStore } from '@store';
import { UIService } from '@services';
import { FeaturesDetailsOverlayTypeEnum } from '../../installation-overlay.model';
import { ViConfirmDialogService, ViToastService } from '@vi-iot/ui/index';
import { TranslateService } from '@ngx-translate/core';
import { PVPowerPrioritizationService } from './pv-power-prioritization.service';

@Component({
  selector: 'app-pv-power-prioritization-overlay',
  templateUrl: './pv-power-prioritization-overlay.component.html',
  styleUrls: ['./pv-power-prioritization-overlay.component.scss'],
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class PvPowerPrioritizationOverlayComponent implements OnInit, OnDestroy {
  isDirty = false;

  pvPowerList$ = new Subject();

  private ngUnsubscribe$ = new Subject<void>();

  constructor(
    private installationFeaturesStore: InstallationFeaturesStore,
    private translateService: TranslateService,
    private pvPowerPrioService: PVPowerPrioritizationService,
    private viConfirmDialogService: ViConfirmDialogService,
    private uiService: UIService,
    private cdr: ChangeDetectorRef,
    private viToastService: ViToastService,
    private dialog: MatDialog,
    private hemsStore: HemsStore
  ) {}

  devices: PVExcessPriorityListItem[] = [];

  private gatewayId: string;

  ngOnInit(): void {
    this.hemsStore.hemsGatewaySerialIfNoError$
      .pipe(takeUntil(this.ngUnsubscribe$))
      .subscribe((gatewaySerial) => {
        this.gatewayId = gatewaySerial;
      });

    this.pvPowerList$
      .pipe(
        switchMap((featureName: string) =>
          this.pvPowerPrioService.getListOfAvailablePVExcessDevicesDetails(featureName)
        ),
        takeUntil(this.ngUnsubscribe$)
      )
      .subscribe(
        ([pvExcessList, deviceNames, types, optimizationDetails, wallboxDevicesWithUInames]) => {
          this.devices = this.pvPowerPrioService.formatDeviceDetails(
            pvExcessList,
            deviceNames,
            types,
            optimizationDetails,
            wallboxDevicesWithUInames
          );

          this.cdr.detectChanges();
          this.updateMoveFlags(this.devices);
        }
      );

    this.pvPowerList$.next(FeatureNames.pvExcessPowerPrioritization);
  }

  openLearnMore(): void {
    this.dialog.open(PvPowerLearnMoreModalComponent, {
      width: '720px',
      autoFocus: false,
    });
  }

  restoreDefaults(): void {
    this.pvPowerList$.next(FeatureNames.pvExcessPowerPrioritizationDefault);

    this.viToastService.info({
      message: this.translateService.instant(
        '+HEMS.SETTINGS.OPTIMIZATIONS.PV_POWER_PRIORITIZATION.RESTORE_DEFAULTS_SUCCESS'
      ),
    });

    this.isDirty = true;
  }

  moveUp(index: number): void {
    if (index <= 0 || this.devices[index].isLocked) {
      return;
    }

    const list = [...this.devices];
    [list[index - 1], list[index]] = [list[index], list[index - 1]];
    this.updateMoveFlags(list);
    this.cdr.markForCheck();
    this.isDirty = true;
  }

  moveDown(index: number): void {
    if (index >= this.devices.length - 1 || this.devices[index].isLocked) {
      return;
    }

    const list = [...this.devices];
    [list[index], list[index + 1]] = [list[index + 1], list[index]];
    this.updateMoveFlags(list);
    this.cdr.markForCheck();
    this.isDirty = true;
  }

  onSave(): void {
    this.installationFeaturesStore.updateFeature(
      FeatureNames.pvExcessPowerPrioritization,
      this.devices.map((device) => ({
        controller: device.deviceId,
        priority: device.priority,
      })),
      FeaturePropertyKey.devices,
      FeaturePropertyKey.value,
      FeatureCommands.setDevices,
      this.gatewayId,
      DeviceIds.Hems,
      {
        successMessage: this.translateService.instant(
          '+HEMS.SETTINGS.OPTIMIZATIONS.PV_POWER_PRIORITIZATION.SAVE_SUCCESS'
        ),
        errorMessage: this.translateService.instant(
          '+HEMS.SETTINGS.OPTIMIZATIONS.PV_POWER_PRIORITIZATION.SAVE_ERROR'
        ),
      }
    );
    this.isDirty = false;
  }

  back(): void {
    if (this.isDirty) {
      this.openConfirmationDialog()
        .pipe(first())
        .subscribe((isNavigatingAway) => {
          if (isNavigatingAway) {
            this.uiService.openActiveOverlay(FeaturesDetailsOverlayTypeEnum.HemsSettings);
          }
        });
    } else {
      this.uiService.openActiveOverlay(FeaturesDetailsOverlayTypeEnum.HemsSettings);
    }
  }

  close(): void {
    if (this.isDirty) {
      this.openConfirmationDialog()
        .pipe(first())
        .subscribe((isNavigatingAway) => {
          if (isNavigatingAway) {
            this.uiService.openActiveOverlay(FeaturesDetailsOverlayTypeEnum.HemsSettings);
          }
        });
    } else {
      this.uiService.closeActiveOverlay();
    }
  }

  ngOnDestroy(): void {
    this.ngUnsubscribe$.next();
    this.ngUnsubscribe$.complete();
  }

  private openConfirmationDialog(): Observable<boolean> {
    return this.viConfirmDialogService.open({
      title: this.translateService.instant(
        '+HEMS.SETTINGS.OPTIMIZATIONS.PV_POWER_PRIORITIZATION.UNSAVED_CHANGES.TITLE'
      ),
      content: this.translateService.instant(
        '+HEMS.SETTINGS.OPTIMIZATIONS.PV_POWER_PRIORITIZATION.UNSAVED_CHANGES.CONTENT'
      ),
      confirmBtn: this.translateService.instant('GENERAL.OK'),
      cancelBtn: this.translateService.instant('GENERAL.BUTTON.CANCEL'),
    });
  }

  private updateMoveFlags(list: PVExcessPriorityListItem[]): void {
    const firstMovable = list.findIndex((d) => !d.isLocked);
    const lastMovable = list.length - 1;
    this.devices = list.map((device, i) => ({
      ...device,
      priority: i + 1,
      ...(device.isLocked
        ? {}
        : {
            canMoveUp: i > firstMovable,
            canMoveDown: i < lastMovable,
          }),
    }));
    this.cdr.markForCheck();
  }
}
